import {HistoricalMedia} from './historicalMedia';
import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {enableMediaAction} from './enableMediaAction';
import Select from 'react-select';
import {getMediaHistoryAction} from "./getMediaHistoryAction";
import {DisableMediaPayload} from './disableMediaAction';
import { getGuestDetailsAction } from '../../../guestDetailsAction';
import { IAppState } from '../../../../core/redux/IAppState';
import { getResource } from '../../../../language/resources/getResource';

type Props = {
  historicalMedia: HistoricalMedia;
  disableMedia: (disableMediaPayload) => void;
  disableMediaPayload?: DisableMediaPayload;
  hideConfirmationMessage:  () => void;
  canDisableMedia: boolean;
  displayDeactivatedMediaHistory: boolean;
  operatorId: string;
}

export const MediaStatusSelector = (props: Props) => {
  const dispatch = useDispatch();
  const resources = useSelector((state: IAppState) => state.locale);
  const baseOptions = useMemo(() => [
    {value: getResource(resources, 'MEDIA_HISTORY_MODAL_ACTIVE'), label: getResource(resources, 'MEDIA_HISTORY_MODAL_ACTIVE')},
    ], [resources]);
  const optionsWithDisabled = useMemo(() => [
    {value: getResource(resources, 'MEDIA_HISTORY_MODAL_ACTIVE'), label: getResource(resources, 'MEDIA_HISTORY_MODAL_ACTIVE')},
    {value: getResource(resources, 'MEDIA_STATUS_SELECTOR_DISABLED'), label: getResource(resources, 'MEDIA_STATUS_SELECTOR_DISABLED')},
  ], [resources]);
  const [selected, setSelected] = React.useState(baseOptions[0]);
  const [options, setOptions] = React.useState(baseOptions);
  const {
   userId,
   resortAbbreviation,
  } = useSelector((state: IAppState) => ({
    userId: state.userState.userId,
    resortAbbreviation: state.selectedResort.resortAbbreviation,
  }));


  useEffect(() => {
    if (props.canDisableMedia) {
      setOptions(optionsWithDisabled);
    } else {
      setOptions(baseOptions);
    }
  }, [props.canDisableMedia, baseOptions, optionsWithDisabled]);


  const dtlDisabled = props.historicalMedia.dtlDisabled;
  const mediaStatusPayload = {
    userId: userId,
    resortAbbreviation: resortAbbreviation,
    chipId: props.historicalMedia.chipID,
    mediaId: props.historicalMedia.mediaID,
    masterGuestId: props.historicalMedia.guestId,
    operatorId: props.operatorId,
  }

  const enableMedia = enableMediaAction(mediaStatusPayload, props.historicalMedia.guestId);

  const handleSelected = useCallback((statusCode: number) => {
    statusCode === 1 
      ? setSelected({value: getResource(resources, 'MEDIA_HISTORY_MODAL_ACTIVE'), label: getResource(resources, 'MEDIA_HISTORY_MODAL_ACTIVE')})
      : setSelected({value: getResource(resources, 'MEDIA_HISTORY_MODAL_INACTIVE'), label: getResource(resources, 'MEDIA_HISTORY_MODAL_INACTIVE')});
  }, [resources])

  const handleChange = (event) => {
    const status = event.value
    setSelected({value: status, label: status});
    if (status === getResource(resources, 'MEDIA_HISTORY_MODAL_ACTIVE') && props.historicalMedia.statusCode === 2) { // Only dispatch action if new status is changed from the original
      dispatch(enableMedia);
      setTimeout(()=> {
        dispatch(getMediaHistoryAction(props.historicalMedia.guestId))
        dispatch(getGuestDetailsAction(props.historicalMedia.guestId))
      }, 500)   
    } else if (status === getResource(resources, 'MEDIA_STATUS_SELECTOR_DISABLED')) {
      props.disableMedia(mediaStatusPayload);
    }
    if (status !== getResource(resources, 'MEDIA_STATUS_SELECTOR_DISABLED')) {
      props.hideConfirmationMessage();
    }
  }

  // Display code of 1 = active
  useEffect(() => {
    if (props.historicalMedia.statusCode === 1) {
      setSelected({
        value: getResource(resources, 'MEDIA_HISTORY_MODAL_ACTIVE'),
        label: getResource(resources, 'MEDIA_HISTORY_MODAL_ACTIVE'),
      })
    } else {
      setSelected({
        value: getResource(resources, 'MEDIA_HISTORY_MODAL_INACTIVE'),
        label: getResource(resources, 'MEDIA_HISTORY_MODAL_INACTIVE'),
      })
    }
  }, [props.historicalMedia.statusCode, resources]);

  useEffect(() => { // If this media is about to be disabled but then the user cancels and tries to disable a different media this will reset
    if (props.disableMediaPayload && props.disableMediaPayload.mediaId !== props.historicalMedia.mediaID) {
      handleSelected(props.historicalMedia.statusCode);
    } else if (!props.disableMediaPayload) {
      handleSelected(props.historicalMedia.statusCode); // If user starts disabling then cancels on the confirmation modal this resets
    }
  }
  , [props.disableMediaPayload, handleSelected, props.historicalMedia.statusCode, props.historicalMedia.mediaID])


  return (
    props.displayDeactivatedMediaHistory 
      ? (props.historicalMedia.statusCode === 1 && !dtlDisabled)
        ? <Select options={options} value={selected} onChange={handleChange}/>
        : <div>{getResource(resources, 'MEDIA_STATUS_SELECTOR_DEACTIVATED')}</div>
      : !dtlDisabled 
        ? <Select options={options} value={selected} onChange={handleChange}/>
        : <div>{getResource(resources, 'MEDIA_STATUS_SELECTOR_DISABLED')}</div>
  );
}